if (!customElements.get('byte-sticky-atc')) {
  const WISHLIST_SOURCE_SELECTOR = '.byte-wishlistify-product-page';
  const WISHLIST_ICON_SELECTOR = '.byte-wishlistify-icon-svg';
  const WISHLIST_EMPTY_FILLS = ['', 'none', 'transparent'];
  const IC_YASTIK_HIGHLIGHT_CLASS = 'is-sticky-scroll-highlight';
  const IC_YASTIK_SCROLL_FALLBACK_MS = 800;
  const DESKTOP_MEDIA_QUERY = '(min-width: 750px)';

  class ByteStickyAtc extends HTMLElement {
    constructor() {
      super();

      this.isVisible = false;
      this.intersectionObserver = null;
      this.submitObserver = null;
      this.submitResizeObserver = null;
      this.headerResizeObserver = null;
      this.wishlistObserver = null;
      this.isScrollQueued = false;
      this.isWishlistSyncQueued = false;
      this.isQuantitySyncing = false;
      this.headerOverlayHeight = 0;
      this.boundHandleResize = this.handleResize.bind(this);
      this.boundHandleScroll = this.handleScroll.bind(this);
      this.variantChangeUnsubscriber = null;
      this.boundHandleClick = this.handleClick.bind(this);
      this.boundHandleVariantChange = this.handleVariantChange.bind(this);
      this.boundHandleStickyQuantityChange = this.handleStickyQuantityChange.bind(this);
      this.boundHandleSourceQuantityChange = this.handleSourceQuantityChange.bind(this);
    }

    connectedCallback() {
      this.#cacheElements();
      if (!this.sourceSubmit) return;

      this.#bindEvents();
      this.#observeSource();
      this.#syncSubmitState();
      this.#syncSubmitWidth();
      this.#observeWishlistSource();
      this.#syncWishlistButton();
      this.#bindQuantitySync();
    }

    disconnectedCallback() {
      this.#unbindEvents();
      this.#setVisibility(false);
    }

    handleClick(event) {
      const actionTrigger = event.target.closest('[data-sticky-atc-action]');
      if (!actionTrigger) return;

      event.preventDefault();

      if (actionTrigger.dataset.stickyAtcAction === 'submit') {
        this.submit();
        return;
      }

      if (actionTrigger.dataset.stickyAtcAction === 'wishlist') {
        this.toggleWishlist();
      }
    }

    handleResize() {
      this.#observeSourceIntersection();
      this.#syncSubmitWidth();
      this.#updateVisibilityFromSource();
    }

    handleScroll() {
      if (this.isScrollQueued) return;

      this.isScrollQueued = true;
      requestAnimationFrame(() => {
        this.isScrollQueued = false;
        this.#updateVisibilityFromSource();
      });
    }

    handleVariantChange(event) {
      const sectionId = event?.data?.sectionId;
      if (sectionId !== this.sectionId) return;

      this.#updatePrice();
      this.#syncSubmitState();
      this.#syncQuantityFromSource();
    }

    submit() {
      if (!this.sourceSubmit) return;

      if (this.#requiresVariantSelection()) {
        this.#promptVariantSelection();
        return;
      }

      if (this.sourceSubmit.hasAttribute('disabled')) return;
      if (this.sourceSubmit.getAttribute('aria-disabled') === 'true') return;

      if (window.matchMedia(DESKTOP_MEDIA_QUERY).matches) {
        this.#scrollToIcYastik();
        return;
      }

      this.#syncQuantityToSource();
      this.sourceSubmit.click();
    }

    handleStickyQuantityChange() {
      this.#syncQuantityToSource();
    }

    handleSourceQuantityChange() {
      this.#syncQuantityFromSource();
    }

    toggleWishlist() {
      const wishlistSource = this.#wishlistSource;
      if (!wishlistSource) return;
      if (wishlistSource.hasAttribute('disabled')) return;

      wishlistSource.click();
    }

    #requiresVariantSelection() {
      if (!this.productInfo) return false;
      if (!this.productInfo.querySelector('variant-selects')) return false;

      return this.productInfo.dataset.variantSelected === 'false';
    }

    #promptVariantSelection() {
      const productInfo = this.productInfo;
      if (!productInfo) return;

      if (typeof productInfo.showVariantSelectionError === 'function') {
        productInfo.showVariantSelectionError();
        return;
      }

      const variantSelectors = productInfo.querySelector('variant-selects');
      variantSelectors?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    #cacheElements() {
      this.productInfo = this.closest('product-info');
      this.sectionId = this.dataset.sectionId;
      this.sourceSubmit = this.productInfo?.querySelector(`#ProductSubmitButton-${this.sectionId}`);
      this.stickySubmit = this.querySelector('[data-sticky-atc-action="submit"]');
      this.stickySubmitTitle = this.querySelector('[data-sticky-atc-submit-title]');
      this.priceElement = this.querySelector('[data-sticky-atc-price]');
      this.wishlistButton = this.querySelector('[data-sticky-atc-action="wishlist"]');
      this.quantityWrapper = this.querySelector('[data-sticky-atc-quantity-wrapper]');
      this.stickyQuantityInput = this.querySelector('[data-sticky-atc-quantity] .quantity__input');
      this.sourceQuantityInput = this.productInfo?.querySelector(`#Quantity-${this.sectionId}`);
      this.header = document.querySelector('.section-header');
    }

    #bindEvents() {
      this.addEventListener('click', this.boundHandleClick);

      window.addEventListener('resize', this.boundHandleResize);
      window.addEventListener('scroll', this.boundHandleScroll, { passive: true });

      if (typeof subscribe === 'function') {
        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.boundHandleVariantChange
        );
      }
    }

    #unbindEvents() {
      this.removeEventListener('click', this.boundHandleClick);
      window.removeEventListener('resize', this.boundHandleResize);
      window.removeEventListener('scroll', this.boundHandleScroll);
      this.variantChangeUnsubscriber?.();
      this.intersectionObserver?.disconnect();
      this.submitObserver?.disconnect();
      this.submitResizeObserver?.disconnect();
      this.headerResizeObserver?.disconnect();
      this.wishlistObserver?.disconnect();
      this.quantityObserver?.disconnect();
      this.stickyQuantityInput?.removeEventListener('change', this.boundHandleStickyQuantityChange);
      this.sourceQuantityInput?.removeEventListener('change', this.boundHandleSourceQuantityChange);
    }

    #observeSource() {
      this.#observeSourceIntersection();

      this.submitObserver = new MutationObserver(() => this.#syncSubmitState());
      this.submitObserver.observe(this.sourceSubmit, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class'],
        childList: true,
        subtree: true,
      });

      this.submitResizeObserver = new ResizeObserver(() => this.#syncSubmitWidth());
      this.submitResizeObserver.observe(this.sourceSubmit);

      if (this.header) {
        this.headerResizeObserver = new ResizeObserver(() => {
          this.#observeSourceIntersection();
          this.#updateVisibilityFromSource();
        });
        this.headerResizeObserver.observe(this.header);
      }

      this.#updateVisibilityFromSource();
    }

    #observeSourceIntersection() {
      if (!this.sourceSubmit) return;

      this.headerOverlayHeight = this.#getHeaderOverlayHeight();
      this.intersectionObserver?.disconnect();
      this.intersectionObserver = new IntersectionObserver(
        () => this.#updateVisibilityFromSource(),
        {
          threshold: 0,
          rootMargin: `-${this.headerOverlayHeight}px 0px 0px 0px`,
        }
      );
      this.intersectionObserver.observe(this.sourceSubmit);
    }

    #updateVisibilityFromSource() {
      this.#setVisibility(!this.#isSourceVisible());
    }

    #isSourceVisible() {
      if (!this.sourceSubmit) return false;
      if (window.matchMedia('(max-width: 749px)').matches) return false;

      const sourceRect = this.sourceSubmit.getBoundingClientRect();
      const headerBottom = this.#getHeaderOverlayHeight();

      return sourceRect.bottom > headerBottom && sourceRect.top < window.innerHeight;
    }

    #getHeaderOverlayHeight() {
      if (this.header) {
        return Math.max(0, Math.round(this.header.getBoundingClientRect().bottom));
      }

      const cssHeaderHeight = Number.parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height')
      );

      return Number.isFinite(cssHeaderHeight) ? Math.max(0, Math.round(cssHeaderHeight)) : 0;
    }

    #setVisibility(isVisible) {
      if (this.isVisible === isVisible) return;

      this.isVisible = isVisible;
      this.classList.toggle('is-visible', isVisible);
      this.toggleAttribute('inert', !isVisible);
      this.setAttribute('aria-hidden', String(!isVisible));
      document.body.classList.toggle('has-sticky-atc', isVisible);

      if (isVisible) {
        this.#syncSubmitWidth();
        document.body.style.setProperty('--byte-sticky-atc-height', `${this.offsetHeight}px`);
      } else {
        document.body.style.removeProperty('--byte-sticky-atc-height');
      }

      this.#syncWishlistButton();
    }

    get #wishlistSource() {
      const sources = this.productInfo?.querySelectorAll(WISHLIST_SOURCE_SELECTOR) ?? [];

      return Array.from(sources).find((source) => !this.contains(source)) || null;
    }

    #observeWishlistSource() {
      if (!this.productInfo || !this.wishlistButton) return;

      this.wishlistObserver = new MutationObserver((mutations) => {
        const isSourceMutation = mutations.some((mutation) => !this.contains(mutation.target));
        if (isSourceMutation) this.#queueWishlistSync();
      });

      this.wishlistObserver.observe(this.productInfo, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['fill', 'class', 'disabled'],
      });
    }

    #queueWishlistSync() {
      if (this.isWishlistSyncQueued) return;

      this.isWishlistSyncQueued = true;
      requestAnimationFrame(() => {
        this.isWishlistSyncQueued = false;
        this.#syncWishlistButton();
      });
    }

    #isWishlistSourceActive(wishlistSource) {
      if (!wishlistSource) return false;

      const icon =
        wishlistSource.querySelector(WISHLIST_ICON_SELECTOR) || wishlistSource.querySelector('svg');
      const iconFill = icon?.getAttribute('fill')?.trim().toLowerCase();

      if (iconFill !== undefined && iconFill !== null) {
        return !WISHLIST_EMPTY_FILLS.includes(iconFill);
      }

      return (
        wishlistSource.classList.contains('is-active') ||
        wishlistSource.classList.contains('active') ||
        wishlistSource.getAttribute('aria-pressed') === 'true'
      );
    }

    #updatePrice() {
      if (!this.priceElement) return;

      const priceRoot = document.getElementById(`price-${this.sectionId}`);
      if (!priceRoot) return;

      const isOnSale = Boolean(priceRoot.querySelector('.price--on-sale'));
      const priceItem = isOnSale
        ? priceRoot.querySelector('.price__sale .price-item--sale')
        : priceRoot.querySelector('.price__regular .price-item--regular');

      if (!priceItem) return;

      this.priceElement.textContent = priceItem.textContent.trim();
    }

    #syncSubmitWidth() {
      if (!this.sourceSubmit) return;

      if (window.matchMedia('(max-width: 749px)').matches) {
        this.style.removeProperty('--sticky-atc-submit-width');
        return;
      }

      const sourceWidth = Math.round(this.sourceSubmit.getBoundingClientRect().width);
      if (sourceWidth <= 0) return;

      this.style.setProperty('--sticky-atc-submit-width', `${sourceWidth}px`);
    }

    #syncWishlistButton() {
      if (!this.wishlistButton) return;

      const wishlistSource = this.#wishlistSource;

      this.wishlistButton.hidden = !wishlistSource;
      if (!wishlistSource) return;

      const isActive = this.#isWishlistSourceActive(wishlistSource);

      if (this.wishlistButton.classList.contains('is-active') !== isActive) {
        this.wishlistButton.classList.toggle('is-active', isActive);
      }

      if (this.wishlistButton.getAttribute('aria-pressed') !== String(isActive)) {
        this.wishlistButton.setAttribute('aria-pressed', String(isActive));
      }
    }

    #bindQuantitySync() {
      if (!this.quantityWrapper) return;

      if (!this.sourceQuantityInput || !this.stickyQuantityInput) {
        this.quantityWrapper.hidden = true;
        return;
      }

      this.quantityWrapper.hidden = false;
      this.#syncQuantityFromSource();

      this.stickyQuantityInput.addEventListener('change', this.boundHandleStickyQuantityChange);
      this.sourceQuantityInput.addEventListener('change', this.boundHandleSourceQuantityChange);

      this.quantityObserver = new MutationObserver(() => this.#syncQuantityFromSource());
      this.quantityObserver.observe(this.sourceQuantityInput, {
        attributes: true,
        attributeFilter: ['min', 'max', 'step', 'data-min', 'data-max', 'data-cart-quantity'],
      });
    }

    #syncQuantityToSource() {
      if (!this.sourceQuantityInput || !this.stickyQuantityInput || this.isQuantitySyncing) return;

      this.isQuantitySyncing = true;

      if (this.sourceQuantityInput.value !== this.stickyQuantityInput.value) {
        this.sourceQuantityInput.value = this.stickyQuantityInput.value;
        this.sourceQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.isQuantitySyncing = false;
    }

    #syncQuantityFromSource() {
      if (!this.sourceQuantityInput || !this.stickyQuantityInput || this.isQuantitySyncing) return;

      this.isQuantitySyncing = true;

      for (const attribute of ['min', 'max', 'step', 'data-min', 'data-max', 'data-cart-quantity']) {
        const attributeValue = this.sourceQuantityInput.getAttribute(attribute);

        if (attributeValue !== null) {
          this.stickyQuantityInput.setAttribute(attribute, attributeValue);
        } else {
          this.stickyQuantityInput.removeAttribute(attribute);
        }
      }

      if (this.stickyQuantityInput.value !== this.sourceQuantityInput.value) {
        this.stickyQuantityInput.value = this.sourceQuantityInput.value;
        this.stickyQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.isQuantitySyncing = false;
    }

    #getIcYastikRoot() {
      return (
        this.productInfo?.querySelector(`byte-ic-yastik[data-section-id="${this.sectionId}"]`) ||
        this.productInfo?.querySelector('byte-ic-yastik') ||
        document.querySelector(`byte-ic-yastik[data-section-id="${this.sectionId}"]`)
      );
    }

    #getIcYastikTarget() {
      const icYastikRoot = this.#getIcYastikRoot();
      if (!icYastikRoot) return null;

      return icYastikRoot.querySelector('[data-ic-yastik-inline]') || icYastikRoot;
    }

    #getLayoutDocumentTop(element) {
      let top = 0;
      let currentElement = element;

      while (currentElement) {
        top += currentElement.offsetTop;
        currentElement = currentElement.offsetParent;
      }

      return top;
    }

    #getIcYastikScrollTop() {
      const icYastikRoot = this.#getIcYastikRoot();
      const scrollAnchor =
        this.productInfo ||
        this.productInfo?.querySelector('.product.grid') ||
        icYastikRoot ||
        this.productInfo?.querySelector(`#ProductInfo-${this.sectionId}`);

      if (!scrollAnchor) return 0;

      const headerOffset = this.#getHeaderOverlayHeight();

      return Math.max(0, this.#getLayoutDocumentTop(scrollAnchor) - headerOffset);
    }

    #scrollToIcYastik() {
      const icYastikTarget = this.#getIcYastikTarget();

      if (!icYastikTarget) {
        this.#syncQuantityToSource();
        this.sourceSubmit.click();
        return;
      }

      const targetScrollTop = this.#getIcYastikScrollTop();
      const playAnimation = () => this.#playIcYastikBreathAnimation(icYastikTarget);
      const scrollDistance = Math.abs(window.scrollY - targetScrollTop);

      if (scrollDistance <= 2) {
        playAnimation();
        return;
      }

      if ('onscrollend' in window) {
        window.addEventListener('scrollend', playAnimation, { once: true });
      } else {
        window.setTimeout(playAnimation, IC_YASTIK_SCROLL_FALLBACK_MS);
      }

      window.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth',
      });
    }

    #playIcYastikBreathAnimation(target) {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      const animationTarget =
        target.closest('byte-ic-yastik') || target;

      animationTarget.classList.remove(IC_YASTIK_HIGHLIGHT_CLASS);
      void animationTarget.offsetWidth;
      animationTarget.classList.add(IC_YASTIK_HIGHLIGHT_CLASS);

      animationTarget.addEventListener(
        'animationend',
        () => {
          animationTarget.classList.remove(IC_YASTIK_HIGHLIGHT_CLASS);
        },
        { once: true }
      );
    }

    #syncSubmitState() {
      if (!this.sourceSubmit || !this.stickySubmit) return;

      const requiresVariantSelection = this.#requiresVariantSelection();
      const isDisabled =
        !requiresVariantSelection &&
        (this.sourceSubmit.hasAttribute('disabled') ||
          this.sourceSubmit.getAttribute('aria-disabled') === 'true');
      const isLoading = this.sourceSubmit.classList.contains('loading');
      const sourceTitle = this.sourceSubmit.querySelector('.submit-title')?.textContent?.trim();

      this.stickySubmit.toggleAttribute('disabled', isDisabled);
      this.stickySubmit.removeAttribute('aria-disabled');
      this.stickySubmit.classList.toggle('loading', isLoading);
      this.stickySubmit.classList.toggle('is-loading', isLoading);
      this.stickySubmit.classList.toggle('is-variant-required', requiresVariantSelection);

      const spinner = this.stickySubmit.querySelector('.loading__spinner');
      spinner?.classList.toggle('hidden', !isLoading);

      if (sourceTitle && this.stickySubmitTitle) {
        this.stickySubmitTitle.textContent = sourceTitle;
      }
    }
  }

  customElements.define('byte-sticky-atc', ByteStickyAtc);
}
